import {
  useFindAllCompaniesQuery,
  useFindAllHardwareQuery,
  useFindAllOrdersQuery,
  useFindOneRmaQuery,
  useRemoveRmaMutation,
} from '../../api/api-hooks';
import {
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ItemOptionsBar from '../../components/ItemOptionsBar';
import { nullStringCheck } from '../../utils/stringManipulation';
import { Item } from '../../utils/sharedTypes';
import { truncateISOString } from '../../utils/dateTime';
import { RmaItemForm } from './RmaTypes';
import { Autocomplete } from '@mui/material';
import RmaHistoryDisplay from './RmaHistoryDisplay';
import useRMAMiddleware from '../../hooks/useRMAMiddleware';
import { itemValidator } from './RmaUtils';
import useLptRmaItemCreator from './useLptRmaItemCreator';
import useLptRmaItemUpdater from './useLptRmaItemUpdater';
import HardwareManagementButton from '../hardware/HardwareManagementButton';

interface Props {
  selectedRmas: Item[];
  setSelectedRmas: React.Dispatch<React.SetStateAction<Item[]>>;
  isHistory: boolean;
  editModalOpenFlag: boolean;
  setEditModalOpenFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

const addFormInitialState: RmaItemForm = {
  nameId: '',
  company: null,
  contactName: '',
  email: '',
  phoneNumber: '',
  hardware: null,
  issue: '',
  status: 'In-Transit',
  notes: '',
  solution: '',
  shipDate: '',
  repairPrice: '',
  trackingNumber: '',
  shipper: '',
  creationDate: truncateISOString(new Date().toISOString()),
  isRepaired: undefined,
  order: null,
};

const LptRmaOptionsBar = ({
  selectedRmas,
  setSelectedRmas,
  isHistory,
  editModalOpenFlag,
  setEditModalOpenFlag,
}: Props) => {
  const [deleteRma] = useRemoveRmaMutation();
  const rmaCreator = useLptRmaItemCreator(isHistory);
  const rmaUpdater = useLptRmaItemUpdater();
  const rmaMiddleware = useRMAMiddleware();
  const { data: allHardware } = useFindAllHardwareQuery({});
  const { data: companies } = useFindAllCompaniesQuery({});
  const { data: orders } = useFindAllOrdersQuery({});
  if (
    allHardware === undefined ||
    companies === undefined ||
    orders === undefined
  )
    return <></>;
  const filteredOrders = orders.filter((order) => !order.completed);
  return (
    <>
      <ItemOptionsBar
        selectedItems={selectedRmas}
        setSelectedItems={setSelectedRmas}
        handlerMiddleware={rmaMiddleware}
        itemValidator={itemValidator}
        itemDeleter={deleteRma}
        itemName='RMA'
        formInstructions='RMA ID, hardware, company, contact name, contact email, creation date, and if an order is Closed, ship date, are required.'
        addFormInitialState={addFormInitialState}
        editModalOpen={editModalOpenFlag}
        setEditModalOpen={setEditModalOpenFlag}
        childrenCreator={(addFormState: RmaItemForm, handler: any) => (
          <Grid container spacing={4}>
            <Grid container xs={6} item direction='column' spacing={1}>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'company');
                  }}
                  value={addFormState.company}
                  options={companies}
                  getOptionLabel={(company) => company.companyName}
                  style={{ width: 300, marginTop: '.5rem' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Company'
                      name='company'
                      variant='outlined'
                      id='company'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'hardware');
                  }}
                  value={addFormState.hardware}
                  options={allHardware.filter((item) => {
                    if (!addFormState.company) return true;
                    return item.companies.some(
                      (company) => company.id === addFormState.company?.id
                    );
                  })}
                  getOptionLabel={(hardware) => hardware.serial}
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Hardware'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <HardwareManagementButton />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='nameId'
                  label='RMA #'
                  fullWidth
                  value={nullStringCheck(addFormState.nameId)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <Grid container alignItems='center' spacing={1}>
                  <Grid item>
                    <Typography>Creation Date: </Typography>
                  </Grid>
                  <Grid item>
                    <TextField
                      type='date'
                      margin='dense'
                      name='creationDate'
                      value={truncateISOString(
                        nullStringCheck(addFormState.creationDate)
                      )}
                      onChange={handler}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='contactName'
                  label='Contact Name'
                  fullWidth
                  value={nullStringCheck(addFormState.contactName)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='email'
                  type='email'
                  label='Contact Email'
                  fullWidth
                  value={nullStringCheck(addFormState.email)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='phoneNumber'
                  label='Contact Phone #'
                  fullWidth
                  value={nullStringCheck(addFormState.phoneNumber)}
                  onChange={handler}
                />
              </Grid>
            </Grid>

            <Grid container xs={6} item direction='column'>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'order');
                  }}
                  value={addFormState.order}
                  options={filteredOrders}
                  getOptionLabel={(order) =>
                    order.purchaseOrderName
                      ? order.purchaseOrderName
                      : order.orderDate
                  }
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Order'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='issue'
                  label='Issue'
                  fullWidth
                  value={nullStringCheck(addFormState.issue)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='notes'
                  label='Notes'
                  fullWidth
                  value={nullStringCheck(addFormState.notes)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='solution'
                  label='Solution'
                  fullWidth
                  value={nullStringCheck(addFormState.solution)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <Typography>Status:</Typography>
              </Grid>
              <Grid item>
                <Select
                  name='status'
                  value={addFormState.status}
                  fullWidth
                  onChange={handler}
                >
                  <MenuItem value='In-Transit'>In-Transit</MenuItem>
                  <MenuItem value='Received'>Received</MenuItem>
                  <MenuItem value='Closed'>Closed</MenuItem>
                </Select>
              </Grid>
              {addFormState.status === 'Closed' && (
                <>
                  <Grid item>
                    <Grid container alignItems='center' spacing={1}>
                      <Grid item>
                        <Typography>Ship Date: </Typography>
                      </Grid>
                      <Grid item>
                        <TextField
                          type='date'
                          margin='dense'
                          name='shipDate'
                          value={truncateISOString(
                            nullStringCheck(addFormState.shipDate)
                          )}
                          onChange={handler}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <TextField
                      margin='dense'
                      name='repairPrice'
                      label='Repair Price'
                      fullWidth
                      value={nullStringCheck(addFormState.repairPrice)}
                      onChange={handler}
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      margin='dense'
                      name='shipper'
                      label='Shipping Co.'
                      fullWidth
                      value={nullStringCheck(addFormState.shipper)}
                      onChange={handler}
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      margin='dense'
                      name='trackingNumber'
                      label='Tracking #'
                      fullWidth
                      value={nullStringCheck(addFormState.trackingNumber)}
                      onChange={handler}
                    />
                  </Grid>
                </>
              )}
              <Grid style={{ marginTop: '1rem' }} item>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addFormState.isRepaired}
                      name='isRepaired'
                      onChange={handler}
                      color='primary'
                    />
                  }
                  label='Is RMA Repaired?'
                />
              </Grid>
              <Grid style={{ marginTop: '1rem' }} item>
                <Typography>RMA History</Typography>
              </Grid>
              <Grid item>
                <RmaHistoryDisplay
                  serial={
                    addFormState.hardware ? addFormState.hardware.serial : ''
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        itemCreator={rmaCreator}
        itemGetter={useFindOneRmaQuery}
        itemUpdater={rmaUpdater}
      />
    </>
  );
};

export default LptRmaOptionsBar;
